import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Settings = {
  version: number;
  fromNumberId: string | null;
  messagingServiceSid: string | null;
  outboundEnabled: boolean;
  notificationsEnabled: boolean;
  recipients: string[];
  consentConfirmed: boolean;
};
type Message = {
  id: string;
  direction: "inbound" | "outbound";
  from: string;
  to: string;
  body: string;
  mediaCount: number;
  status: string;
  errorCode: string | null;
  createdAt: string;
};
type Data = {
  settings: Settings;
  numbers: { id: string; number: string }[];
  services: { id: string; label: string }[];
  readiness: { ready: boolean; detail: string; checkedAt: string };
  serviceError: string;
  messages: Message[];
};
export function normalizeSmsRecipient(value: string) {
  const digits = value.trim().replace(/[ ().-]/g, "");
  return /^\d{10}$/.test(digits)
    ? `+1${digits}`
    : /^1\d{10}$/.test(digits)
      ? `+${digits}`
      : digits;
}

export function AccountMessagingPanel({
  api,
  csrf,
  accountId,
  customer,
  readOnly = false,
}: {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  customer: boolean;
  readOnly?: boolean | undefined;
}) {
  const path = `/${customer ? "customer" : "admin"}/accounts/${accountId}/messaging`;
  const [data, setData] = useState<Data | null>(null);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [recipients, setRecipients] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState(""),
    [body, setBody] = useState("");
  const [consent, setConsent] = useState(false);
  const settingsAttempt = useRef({ payload: "", key: crypto.randomUUID() });
  const sendAttempt = useRef({ payload: "", key: crypto.randomUUID() });
  const request = useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    request.current?.abort();
    const abort = new AbortController();
    request.current = abort;
    const value = await api<Data>(path, { signal: abort.signal });
    if (abort.signal.aborted) return;
    setData(value);
    setDraft(value.settings);
    setRecipients(value.settings.recipients.join("\n"));
  }, [api, path]);
  useEffect(() => {
    setData(null);
    setDraft(null);
    setError("");
    setNotice("");
    setBody("");
    setTo("");
    setConsent(false);
    settingsAttempt.current = { payload: "", key: crypto.randomUUID() };
    sendAttempt.current = { payload: "", key: crypto.randomUUID() };
    void load().catch((cause) => {
      if (!request.current?.signal.aborted) setError((cause as Error).message);
    });
    return () => request.current?.abort();
  }, [load]);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft || busy || readOnly || customer) return;
    setBusy(true);
    setError("");
    setNotice("");
    const payload = {
      fromNumberId: draft.fromNumberId,
      messagingServiceSid: draft.messagingServiceSid,
      outboundEnabled: draft.outboundEnabled,
      notificationsEnabled: draft.notificationsEnabled,
      consentConfirmed: draft.consentConfirmed,
      expectedVersion: draft.version,
      recipients: recipients
        .split(/[\n,;]+/)
        .map(normalizeSmsRecipient)
        .filter(Boolean),
    };
    const serialized = JSON.stringify(payload);
    if (settingsAttempt.current.payload !== serialized)
      settingsAttempt.current = {
        payload: serialized,
        key: crypto.randomUUID(),
      };
    try {
      await api(`${path}/settings`, {
        method: "POST",
        csrf,
        body: { ...payload, requestKey: settingsAttempt.current.key },
      });
      settingsAttempt.current = { payload: "", key: crypto.randomUUID() };
      await load();
      setNotice("Text and voicemail notification settings saved.");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function send(event: FormEvent) {
    event.preventDefault();
    if (
      busy ||
      readOnly ||
      !consent ||
      !data?.readiness.ready ||
      !data.settings.outboundEnabled
    )
      return;
    setBusy(true);
    setError("");
    setNotice("");
    const payload = {
      to: normalizeSmsRecipient(to),
      body: body.trim(),
      consentConfirmed: true,
    };
    const serialized = JSON.stringify(payload);
    if (sendAttempt.current.payload !== serialized)
      sendAttempt.current = { payload: serialized, key: crypto.randomUUID() };
    try {
      const result = await api<{ message: Message }>(`${path}/send`, {
        method: "POST",
        csrf,
        body: { ...payload, requestKey: sendAttempt.current.key },
      });
      await load();
      if (["unknown", "submitting"].includes(result.message.status))
        setNotice(
          "The submission outcome is not yet known. Check its status; sending it again could create a duplicate.",
        );
      else if (result.message.status === "blocked")
        setNotice(
          "Delivery was blocked. Review sender readiness and notification settings.",
        );
      else {
        setNotice(`Text submitted. Delivery status: ${result.message.status}.`);
        setBody("");
        setConsent(false);
        sendAttempt.current = { payload: "", key: crypto.randomUUID() };
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="cr-panel" aria-label="Texts and voicemail alerts">
      <div className="cr-account-heading">
        <h2>Texts &amp; voicemail alerts</h2>
        <button
          className="cr-button secondary"
          disabled={busy}
          onClick={() => {
            setError("");
            void load().catch((cause) => setError((cause as Error).message));
          }}
        >
          Refresh messages
        </button>
      </div>
      {!data && !error && (
        <p role="status">
          Loading this customer's texts and notification settings…
        </p>
      )}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {data && (
        <>
          <p role="status">
            <strong>
              {data.readiness.ready
                ? "Sender verified"
                : "Text delivery needs setup"}
            </strong>{" "}
            ·{" "}
            {customer
              ? data.readiness.ready
                ? "Text messaging is ready for this account."
                : "Contact Cache River to activate text messaging for this account."
              : data.readiness.detail}
          </p>
          {!customer && draft && (
            <details className="cr-editor" open={!data.settings.fromNumberId}>
              <summary>
                Text delivery &amp; voicemail notification settings
              </summary>
              <form className="cr-form" onSubmit={save}>
                <label>
                  Sender number
                  <select
                    value={draft.fromNumberId ?? ""}
                    disabled={busy || readOnly}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        fromNumberId: event.target.value || null,
                      })
                    }
                  >
                    <option value="">Choose this customer's number</option>
                    {data.numbers.map((number) => (
                      <option key={number.id} value={number.id}>
                        {number.number}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Messaging service
                  <select
                    value={draft.messagingServiceSid ?? ""}
                    disabled={busy || readOnly}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        messagingServiceSid: event.target.value || null,
                      })
                    }
                  >
                    <option value="">Choose an assigned CRTC service</option>
                    {data.services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                </label>
                {data.serviceError && <p role="alert">{data.serviceError}</p>}
                {!data.services.length && (
                  <p>
                    A CRTC messaging service must be assigned before outgoing
                    texts can be enabled. Existing external texting connections
                    are kept until their replacement is verified.
                  </p>
                )}
                <label className="cr-check">
                  <input
                    type="checkbox"
                    checked={draft.outboundEnabled}
                    disabled={
                      busy ||
                      readOnly ||
                      (!data.readiness.ready && !draft.outboundEnabled)
                    }
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        outboundEnabled: event.target.checked,
                      })
                    }
                  />
                  Enable outgoing texts
                </label>
                <label>
                  Voicemail alert recipients
                  <textarea
                    value={recipients}
                    disabled={busy || readOnly}
                    onChange={(event) => setRecipients(event.target.value)}
                    rows={3}
                    placeholder="One telephone number per line"
                  />
                </label>
                <label className="cr-check">
                  <input
                    type="checkbox"
                    checked={draft.consentConfirmed}
                    disabled={busy || readOnly}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        consentConfirmed: event.target.checked,
                      })
                    }
                  />
                  These recipients requested voicemail text alerts.
                </label>
                <label className="cr-check">
                  <input
                    type="checkbox"
                    checked={draft.notificationsEnabled}
                    disabled={
                      busy ||
                      readOnly ||
                      (!data.readiness.ready && !draft.notificationsEnabled)
                    }
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        notificationsEnabled: event.target.checked,
                      })
                    }
                  />
                  Notify these recipients of new voicemail
                </label>
                <p className="cr-form-note">
                  Alerts contain a message count and portal instructions. They
                  do not include voicemail audio or transcripts. A recipient can
                  text STOP to the sender to disable further texts.
                </p>
                <button className="cr-button" disabled={busy || readOnly}>
                  Save text settings
                </button>
              </form>
            </details>
          )}
          {!readOnly && (
            <form className="cr-form" onSubmit={send}>
              <h3>Send a text</h3>
              <label>
                Recipient
                <input
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                  type="tel"
                  required
                  maxLength={25}
                  autoComplete="tel"
                />
              </label>
              <label>
                Message
                <textarea
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  rows={3}
                  required
                  maxLength={480}
                />
              </label>
              <label className="cr-check">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  required
                />
                I have permission to text this recipient.
              </label>
              <button
                className="cr-button"
                disabled={
                  busy ||
                  !consent ||
                  !data.readiness.ready ||
                  !data.settings.outboundEnabled
                }
              >
                {busy ? "Working…" : "Send text"}
              </button>
              {!data.settings.outboundEnabled && (
                <p>
                  Outgoing texts are disabled. Staff can enable them once the
                  sender is verified.
                </p>
              )}
            </form>
          )}
          <h3>Latest texts</h3>
          {!data.messages.length ? (
            <p>
              No texts have been recorded for this customer. Incoming messages
              appear here after the signed Twilio connection is activated.
            </p>
          ) : (
            <ol className="cr-message-list">
              {data.messages.map((message) => (
                <li key={message.id}>
                  <p>
                    <strong>
                      {message.direction === "inbound"
                        ? "Received from"
                        : "Sent to"}{" "}
                      {message.direction === "inbound"
                        ? message.from
                        : message.to}
                    </strong>{" "}
                    · {message.status} ·{" "}
                    {new Date(message.createdAt).toLocaleString()}
                  </p>
                  <p className="cr-message-body">{message.body}</p>
                  {message.mediaCount > 0 && (
                    <p>
                      {message.mediaCount} media{" "}
                      {message.mediaCount === 1
                        ? "attachment was"
                        : "attachments were"}{" "}
                      received. Media viewing is not enabled in this portal.
                    </p>
                  )}
                  {message.errorCode && (
                    <p>Delivery error {message.errorCode}</p>
                  )}
                </li>
              ))}
            </ol>
          )}
          {data.messages.length === 100 && (
            <p>The latest 100 texts are shown.</p>
          )}
        </>
      )}
    </section>
  );
}
